// ============================================================
// ELC Shift — Uploads / Balance Reconcile (v137 / PR2)
//
// The ops-manager bulk leave-balance CORRECTION upload. Round-trip:
//   1. Download a CSV template Shift pre-fills with employee number + name
//      + store (columns A–C). The match back is by employee number, so it is
//      guaranteed — no fuzzy name matching, no "the two systems disagree".
//   2. The manager VLOOKUPs Holiday (days) / TOIL (hours) / Sick (days) from
//      the HR sheet into columns D–F. A blank cell means "leave that number".
//   3. Upload → PREVIEW every change (before → after) → confirm.
//
// The maths + writes are server-side (POST /admin/balances/reconcile, PR1):
// HR's figure becomes the new baseline and Shift keeps deducting future leave
// from it (keep-tracking). This module is the UI shell — it never computes a
// balance itself; it sends the target numbers and renders what the worker
// returns. Boundary matches the rest of Uploads.*: Uploads.balanceReconcile.X.
//
// Public surface (Uploads.balanceReconcile.X — inline handlers in admin.html):
//   downloadTemplate()   — build + download the pre-filled CSV
//   handleDrop(e)        — drop-zone handler
//   handleFile(files)    — file-input handler → parse → dry-run → preview
//   confirmApply()       — write the corrections (dryRun:false) + refresh
//   cancel()             — reset the panel
//   asAtChanged()        - v136.14: the "as at" date changed; re-run the preview
//
// v136.14 (19 Oct 2026): the upload carries an "as at" date ("These HR figures include leave
// taken up to"), default today in Dubai. HR deducts leave only once it has happened, so the
// Worker adds back only rota leave dated on or before it. The SAME date goes with the preview
// and the confirmed write (captured at preview time), checked by the one shared rule.
//
// Pure, unit-tested helpers (exported): buildTemplateCsv, parseTemplateRows,
// parseCsvText.
//
// Free globals consumed (admin.html lexical scope / other bundles):
//   staffData, DB (DB.balances.reconcile), showToast, escapeHtml,
//   XLSX (SheetJS, optional — only for .xlsx uploads),
//   Uploads.balances.formatToilDH / renderToilOverview / loadFromDB
// ============================================================

// ── PURE HELPERS (testable without a DOM) ───────────────────

// Build the CSV template Shift hands the manager. Columns A–C pre-filled
// (employee number, name, store); D–F blank with UNIT-EXPLICIT headers.
import { ensureXlsx } from '../shared/ensure-xlsx.js';   // v136.8: the spreadsheet library loads only when it is used
import { checkAsAt } from '../shared/reconcile-as-at.js';   // v136.14: the same check the Worker makes

// Today in Dubai (UTC+4), YYYY-MM-DD: the default "as at" date and the year the upload is for.
export function dubaiToday() { return new Date(Date.now() + 4 * 3600 * 1000).toISOString().slice(0, 10); }

// v136.14: the per-person line under a holiday / sick figure in the preview. Pure, exported for tests.
export function addBackNote(kind, ab, asAt) {
  if (!ab) return '';
  const n = Number(ab.added_back) || 0;
  const m = Number(ab.after_as_at) || 0;
  return n + ' rota ' + kind + ' day' + (n === 1 ? '' : 's') + ' added back (up to ' + asAt + '), ' + m + ' after';
}

export function buildTemplateCsv(staffList) {
  const esc = (v) => {
    const s = String(v == null ? '' : v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const rows = (staffList || []).slice().sort((a, b) =>
    String(a.store || '').localeCompare(String(b.store || '')) ||
    String(a.name || '').localeCompare(String(b.name || '')));
  const lines = ['Employee No,Name,Store,Holiday (days),TOIL (hours),Sick (days)'];
  rows.forEach(s => lines.push([esc(s.empCode || ''), esc(s.name || ''), esc(s.store || ''), '', '', ''].join(',')));
  return lines.join('\r\n');
}

// Minimal RFC-ish CSV parser — handles quoted fields (commas + escaped quotes)
// so a name like "Smith, John" never shifts the columns.
export function parseCsvText(text) {
  const rows = [];
  const lines = String(text || '').split(/\r?\n/);
  for (const line of lines) {
    if (line === '') continue;
    const cells = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQ) {
        if (ch === '"') { if (line[i + 1] === '"') { cur += '"'; i++; } else inQ = false; }
        else cur += ch;
      } else if (ch === '"') { inQ = true; }
      else if (ch === ',') { cells.push(cur); cur = ''; }
      else cur += ch;
    }
    cells.push(cur);
    rows.push(cells);
  }
  return rows;
}

// Turn the uploaded rows (2D array) into reconcile corrections. Columns are
// found by HEADER NAME (case-insensitive substring) so reordering / extra
// columns don't matter. Blank cell → that field is omitted ("leave it").
export function parseTemplateRows(rows) {
  if (!rows || !rows.length) return { corrections: [], warnings: ['The file is empty.'] };
  const norm = (s) => String(s == null ? '' : s).trim().toLowerCase();
  const header = rows[0].map(norm);
  const findCol = (kw) => header.findIndex(h => h.includes(kw));
  const idx = { emp: findCol('emp'), hol: findCol('holiday'), toil: findCol('toil'), sick: findCol('sick') };
  if (idx.emp < 0) return { corrections: [], warnings: ['No "Employee No" column found — use the downloaded template.'] };
  if (idx.hol < 0 && idx.toil < 0 && idx.sick < 0) {
    return { corrections: [], warnings: ['No Holiday / TOIL / Sick columns found — use the downloaded template.'] };
  }
  const num = (v) => {
    const t = String(v == null ? '' : v).trim().replace(/,/g, '');
    if (t === '') return null;
    const n = parseFloat(t);
    return isFinite(n) ? n : null;
  };
  const corrections = [];
  let skippedBlank = 0, skippedNoEmp = 0;
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    if (!r || !r.some(c => String(c == null ? '' : c).trim())) continue; // wholly blank line
    const emp = String(r[idx.emp] == null ? '' : r[idx.emp]).trim().toUpperCase();
    if (!emp) { skippedNoEmp++; continue; }
    const hol = idx.hol >= 0 ? num(r[idx.hol]) : null;
    const toil = idx.toil >= 0 ? num(r[idx.toil]) : null;
    const sick = idx.sick >= 0 ? num(r[idx.sick]) : null;
    if (hol === null && toil === null && sick === null) { skippedBlank++; continue; }
    corrections.push({ emp_code: emp, holiday_remaining: hol, sick_remaining: sick, toil_hours: toil });
  }
  const warnings = [];
  if (skippedBlank) warnings.push(skippedBlank + ' row(s) left blank — skipped.');
  if (skippedNoEmp) warnings.push(skippedNoEmp + ' row(s) had no employee number — skipped.');
  return { corrections, warnings };
}

// ── STATE (private to this bundle) ──────────────────────────
let _brCorrections = [];
let _brFilename = '';
let _brWarnings = [];   // v136.14: the file's parse warnings, kept so a re-preview shows them again
let _brAsAt = '';   // v136.14: the as-at date the shown preview was built with; the write sends the same one

// v136.14: read the as-at box (blank = today in Dubai, written back so the manager sees it) and check
// it. Returns { year, asAt } or null after a plain message.
function readAsAt() {
  const today = dubaiToday();
  const year = today.slice(0, 4);
  const box = el('brAsAt');
  let raw = box ? String(box.value || '').trim() : '';
  if (!raw) { raw = today; if (box) box.value = today; }
  const chk = checkAsAt(raw, year, today);
  if (chk.error) { toast(chk.error, 'error'); return null; }
  return { year, asAt: chk.asAt };
}

// ── DOM HANDLERS ────────────────────────────────────────────
function el(id) { return document.getElementById(id); }
function toast(msg, kind) { if (typeof showToast === 'function') showToast(msg, kind || 'info'); }

export function downloadTemplate() {
  const list = (typeof staffData !== 'undefined' ? staffData : []).filter(s => s && s.active !== false);
  if (!list.length) { toast('No staff loaded yet — open the People tab first.', 'error'); return; }
  const csv = buildTemplateCsv(list);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'leave-balance-template.csv';
  document.body.appendChild(a); a.click(); a.remove();
  const noEmp = list.filter(s => !s.empCode).length;
  toast('Template downloaded (' + list.length + ' staff)' +
    (noEmp ? ' — ' + noEmp + ' have no employee number and can\'t be matched' : '') +
    '. Fill Holiday/TOIL/Sick, then upload.', noEmp ? 'info' : 'success');
}

export function handleDrop(e) {
  e.preventDefault();
  const dz = el('brDropZone');
  if (dz) { dz.style.borderColor = 'var(--border)'; dz.style.background = 'var(--surface-2)'; }
  const files = e.dataTransfer && e.dataTransfer.files ? Array.from(e.dataTransfer.files) : [];
  if (files.length) handleFile(files);
}

function readRows(file) {
  // v136.8: an .xlsx needs the spreadsheet library, which loads on first use (a failed load rejects with a plain
  // message). Before, a missing library quietly read the .xlsx as CSV.
  const isXlsx = (file.name.split('.').pop() || '').toLowerCase() === 'xlsx';
  return (isXlsx ? ensureXlsx() : Promise.resolve()).then(() => new Promise((resolve, reject) => {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read ' + file.name));
    reader.onload = (ev) => {
      try {
        if (ext === 'xlsx' && typeof XLSX !== 'undefined') {
          const wb = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          resolve(XLSX.utils.sheet_to_json(ws, { header: 1, raw: false, defval: '' }));
        } else {
          resolve(parseCsvText(new TextDecoder('utf-8').decode(ev.target.result)));
        }
      } catch (err) { reject(err); }
    };
    reader.readAsArrayBuffer(file);
  }));
}

export async function handleFile(files) {
  const file = files && files[0];
  if (!file) return;
  _brFilename = file.name;
  let rows;
  try { rows = await readRows(file); }
  catch (e) { toast('Could not read the file: ' + e.message, 'error'); return; }

  const { corrections, warnings } = parseTemplateRows(rows);
  if (!corrections.length) {
    toast(warnings[0] || 'Nothing to update in that file.', 'error');
    return;
  }
  _brCorrections = corrections;
  _brWarnings = warnings;
  await runPreview();
}

// Dry run: the worker computes before/after; nothing is written.
// v136.14: until a preview for the CURRENT date has come back, there is no date to write with, so
// a click on Apply in between is refused, and a refused date hides the old preview.
async function runPreview() {
  _brAsAt = '';
  const when = readAsAt();
  if (!when) { hidePreview(); return; }
  let result;
  try {
    result = await DB.balances.reconcile({ year: when.year, as_at: when.asAt, dryRun: true, corrections: _brCorrections });
  } catch (e) {
    toast('Preview failed: ' + (e && e.message ? e.message : e), 'error');
    hidePreview();
    return;
  }
  _brAsAt = (result && result.as_at) || when.asAt;
  renderPreview(result, _brWarnings);
}

// v136.14: a new as-at date changes every added-back figure, so an open preview is rebuilt rather
// than left showing numbers the write would not use.
export async function asAtChanged() {
  if (!_brCorrections.length) return;
  await runPreview();
}

function hidePreview() { const card = el('brPreviewCard'); if (card) card.style.display = 'none'; }

function fmtToil(h) {
  if (typeof Uploads !== 'undefined' && Uploads.balances && Uploads.balances.formatToilDH) return Uploads.balances.formatToilDH(h);
  return (h == null ? 0 : h) + 'h';
}
function esc(s) { return (typeof escapeHtml === 'function') ? escapeHtml(String(s == null ? '' : s)) : String(s == null ? '' : s); }

function renderPreview(result, warnings) {
  const card = el('brPreviewCard');
  const body = el('brPreviewBody');
  if (!card || !body) return;
  const results = (result && result.results) || [];
  const sum = (result && result.summary) || {};
  const asAt = (result && result.as_at) || _brAsAt;
  const note = (kind, ab) => ab
    ? '<div style="font-size:11px;color:var(--sub);margin-top:2px">' + esc(addBackNote(kind, ab, asAt)) + '</div>' : '';

  const cell = (f, unit, isToil) => {
    if (!f) return '<span style="color:var(--sub)">—</span>';
    const before = isToil ? fmtToil(f.before) : (f.before + (unit || ''));
    const after = isToil ? fmtToil(f.after) : (f.after + (unit || ''));
    if (String(f.before) === String(f.after)) return '<span style="color:var(--sub)">' + esc(after) + '</span>';
    return '<span style="color:var(--red);text-decoration:line-through">' + esc(before) + '</span> → <span style="color:var(--green);font-weight:700">' + esc(after) + '</span>';
  };

  const rowsHtml = results.map(r => {
    const status = !r.matched
      ? '<span class="badge badge-red">no match</span>'
      : r.outOfScope
        ? '<span class="badge badge-amber">out of scope</span>'
        : r.error
          ? '<span class="badge badge-red">error</span>'
          : (r.changed ? '<span class="badge badge-green">will update</span>' : '<span class="badge badge-grey">no change</span>');
    const name = r.name || '—';
    const empCode = r.emp_code || '—';
    const f = r.fields || {};
    const ab = r.addBack || {};
    const dim = (!r.matched || r.outOfScope) ? 'opacity:0.7;' : '';
    return '<tr style="' + dim + '">' +
      '<td translate="no" style="font-family:monospace;font-size:11px">' + esc(empCode) + '</td>' +
      '<td translate="no" style="font-weight:600">' + esc(name) + '</td>' +
      '<td>' + cell(f.holiday, 'd', false) + note('holiday', ab.holiday) + '</td>' +
      '<td>' + cell(f.toil, '', true) + '</td>' +
      '<td>' + cell(f.sick, 'd', false) + note('sick', ab.sick) + '</td>' +
      '<td>' + status + (r.error ? ' <span translate="no" style="font-size:11px;color:var(--red)">' + esc(r.error) + '</span>' : '') + '</td>' +
      '</tr>';
  }).join('');

  const willWrite = sum.toWrite || 0;
  const bits = [];
  if (willWrite) bits.push('<strong>' + willWrite + '</strong> will update');
  if (sum.outOfScope) bits.push('<span style="color:var(--warn-deep)">' + sum.outOfScope + ' out of scope</span>');
  if (sum.unmatched) bits.push('<span style="color:var(--red)">' + sum.unmatched + ' no match</span>');
  const warnHtml = (warnings && warnings.length)
    ? '<div style="font-size:12px;color:var(--warn-deep);margin-bottom:8px">⚠️ ' + warnings.map(w => '<span>' + esc(w) + '</span>').join(' · ') + '</div>' : '';

  // v136.14: say which date the figures were read as at, and what "after" means, above the table.
  const asAtHtml = asAt
    ? '<div style="font-size:12px;color:var(--sub);margin-bottom:8px"><span>HR figures as at ' + esc(asAt) + '</span>. ' +
      '<span>Shift adds back only the rota leave on or before this date. Leave after it is deducted as it happens, so "after" is the HR figure less those days.</span></div>'
    : '';

  body.innerHTML =
    warnHtml +
    asAtHtml +
    '<div style="font-size:13px;margin-bottom:10px">' + (bits.join(' · ') || 'No changes to apply.') + '</div>' +
    '<div style="max-height:340px;overflow:auto"><table class="data-table" style="font-size:12px">' +
    '<thead><tr><th>Emp No</th><th>Name</th><th>🌴 Holiday</th><th>⏱ TOIL</th><th>🤒 Sick</th><th>Status</th></tr></thead>' +
    '<tbody>' + rowsHtml + '</tbody></table></div>' +
    '<div style="display:flex;gap:8px;margin-top:12px">' +
    '<button class="btn btn-green" onclick="Uploads.balanceReconcile.confirmApply()"' + (willWrite ? '' : ' disabled') + '>✅ Apply ' + (willWrite || 0) + ' change' + (willWrite === 1 ? '' : 's') + '</button>' +
    '<button class="btn btn-outline" onclick="Uploads.balanceReconcile.cancel()">Cancel</button>' +
    '</div>';
  card.style.display = '';
}

// v136.8 - what an apply did, from the Worker's answer. Failures come from result.failures (v136.8 Worker) or,
// from an older Worker, the rows in results that carry an error. Pure, exported for tests.
export function applyOutcome(result) {
  const r = result || {};
  const sum = r.summary || {};
  const wrote = Number(sum.wrote) || 0;
  let failures = Array.isArray(r.failures) ? r.failures.slice() : [];
  if (!failures.length && Array.isArray(r.results)) {
    failures = r.results.filter((x) => x && x.error).map((x) => ({ staff_id: x.staff_id, name: x.name || null, emp_code: x.emp_code || null, error: x.error }));
  }
  const failed = failures.length;
  const tried = wrote + failed;
  const message = failed
    ? 'Applied ' + wrote + ' of ' + tried + ' balance corrections. ' + failed + ' failed: ' +
      failures.map((f) => f.name || f.emp_code || ('staff #' + f.staff_id)).join(', ') + '.'
    : 'Applied ' + wrote + ' balance correction' + (wrote === 1 ? '' : 's') + '.';
  return { wrote, failed, failures, message };
}

function showApplyFailures(out) {
  const card = el('brPreviewCard');
  const body = el('brPreviewBody');
  if (!card || !body) return;
  body.innerHTML =
    '<div style="font-size:13px;margin-bottom:10px;color:var(--red)"><strong>' + esc(out.failed) + ' not updated.</strong> ' +
    esc(out.wrote) + ' applied. Fix the problem and upload the file again: rows that already applied simply land on the same figures.</div>' +
    '<div style="max-height:340px;overflow:auto"><table class="data-table" style="font-size:12px">' +
    '<thead><tr><th>Emp No</th><th>Name</th><th>Problem</th></tr></thead><tbody>' +
    out.failures.map((f) => '<tr><td translate="no" style="font-family:monospace;font-size:11px">' + esc(f.emp_code || '-') + '</td>' +
      '<td translate="no" style="font-weight:600">' + esc(f.name || ('staff #' + f.staff_id)) + '</td>' +
      '<td translate="no" style="color:var(--red)">' + esc(f.error || '') + '</td></tr>').join('') +
    '</tbody></table></div>' +
    '<div style="display:flex;gap:8px;margin-top:12px"><button class="btn btn-outline" onclick="Uploads.balanceReconcile.cancel()">Close</button></div>';
  card.style.display = '';
}

export async function confirmApply() {
  if (!_brCorrections.length) { cancel(); return; }
  // v136.14: write with the date the preview was built with (and its year), never a re-read of the box.
  const asAt = _brAsAt;
  if (!asAt) { toast('Preview the file with a valid "as at" date first.', 'error'); return; }
  const year = asAt.slice(0, 4);
  let result;
  try {
    result = await DB.balances.reconcile({ year, as_at: asAt, dryRun: false, source: _brFilename || 'leave-balance upload', corrections: _brCorrections });
  } catch (e) {
    toast('Apply failed: ' + (e && e.message ? e.message : e), 'error');
    return;
  }
  // v136.8 (16 Oct audit): it said "Applied N" even when some rows failed. Now a partial apply says so and
  // keeps the card open with the people who were NOT updated.
  const out = applyOutcome(result);
  if (out.failed) {
    toast(out.message, 'error');
    showApplyFailures(out);
  } else {
    toast(out.message, 'success');
    cancel();
  }
  // Refresh the balances the overview reads, then re-render it.
  try {
    if (typeof Uploads !== 'undefined' && Uploads.balances) {
      if (Uploads.balances.loadFromDB) await Uploads.balances.loadFromDB();
      if (Uploads.balances.renderToilOverview) Uploads.balances.renderToilOverview();
    }
  } catch (_e) {}
}

export function cancel() {
  _brCorrections = []; _brFilename = ''; _brAsAt = ''; _brWarnings = [];
  const card = el('brPreviewCard'); if (card) card.style.display = 'none';
  const fi = el('brFileInput'); if (fi) fi.value = '';
}
